import { MultiSchoolAccountModal } from "@/components/MutilSchoolAccountModal/MultiSchoolAccountModal";
import { SessionExpiredModal } from "@/components/SessionExpiredModal/SessionExpiredModal";
import { SignedOutModal } from "@/components/SignedOutModal/SignedOutModal";
import { captureAppError } from "@/utils/monitoring/sentry";
import * as Sentry from "@sentry/react";
import {
  ContentContainer,
  PageHeader,
} from "@twinkltech/curriculum-architect-component-library";
import {
  ApiError,
  useCurrentUser,
} from "@twinkltech/curriculum-architect-domain-hooks";
import React, { ComponentProps, useRef, useState } from "react";

import { LoadingPage } from "@/pages/LoadingPage/LoadingPage";
import { MainLayout } from "@/pages/MainLayout/MainLayout";


const ONE_HOUR = 3600000; // milliseconds

const withPermission = (Component: React.ComponentType) => {
  const WrappedComponent = <T extends React.ComponentType>(
    props: ComponentProps<T>
  ) => {
    const { data, error, isLoading } = useCurrentUser();

    const [sessionExpired, setSessionExpired] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    // Loading should always be shown before all other states to help prevent flashes of
    // other states that may be outdated or incorrect while new data is being processed
    if (isLoading) {
      return (
        <MainLayout>
          <LoadingPage />
        </MainLayout>
      );
    }

    // If there we want to handle them before any other states (bar loading)
    if (error) {
      // Handle specific API errors
      if (error instanceof ApiError) {
        // Note: Cloudfront is causing 4xx errors to not be returned due to CORS issues.
        // leaving code here as it's correct, but we need to assumed that the sign-in
        // modal is the only valid response right now until CF is "fixed"
        if (error.status === 401) {
          return (
            <MainLayout>
              <SignedOutModal />
            </MainLayout>
          );
        }

        captureAppError(error, {
          action: "fetch-current-user",
          area: "permission",
          extra: {
            status: error.status,
          },
          tags: {
            errorType: "api-error",
          },
        });
      } else {
        // Otherwise fallback to a generic error
        captureAppError(error, {
          action: "fetch-current-user",
          area: "permission",
          tags: {
            errorType: "unhandled-error",
          },
        });
      }

      return (
        <MainLayout>
          {/* Note: Cloudfront is causing 4xx errors to not be returned due to CORS issues.
          leaving code here as it's correct, but we need to assumed that the sign-in
          modal is the only valid response right now until CF is "fixed" */}
          {/* <GenericErrorModal /> */}

          <SignedOutModal />
        </MainLayout>
      );
    }

    // sessionExpire will be true of the user had previously successfully logged in
    if (sessionExpired) {
      return (
        <MainLayout>
          <SessionExpiredModal />
        </MainLayout>
      );
    }

    // Deny Access to school users with multiple schools
    if (data?.persona.accountType === "multi-school") {
      return (
        <MainLayout>
          <MultiSchoolAccountModal />
        </MainLayout>
      );
    }

    // User is logged in are we are able to determine session expiratin
    if (data && !sessionExpired) {
      let expiration = new Date().getTime() + ONE_HOUR;
      if (data?.jwt?.expiration) {
        expiration = new Date(data.jwt.expiration).getTime();
      }

      Sentry.setUser({ id: data.userId });

      const now = new Date().getTime();
      const timeUntilExpiration =
        // there is a fun bug with setTimeout where it will not run if the time is greater than 2147483647
        // so we need to cap it at 2000000. If the JWT expiry is under that, we will just use it as is.
        expiration - now > 2000000000 ? 2000000000 : expiration - now;

      if (timeUntilExpiration > 0) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          setSessionExpired(true);
        }, timeUntilExpiration);
      } else {
        setSessionExpired(true);
      }

      // If the user is logged in and has an adequate subscription, render the component
      return <Component {...props} />;
    }

    // Fallthrough Catch All
    captureAppError(new Error("Unknown state received by withPermission"), {
      action: "render",
      area: "permission",
    });
    return (
      <MainLayout>
        <ContentContainer bottomPadding="none">
          <PageHeader>
            <PageHeader.Title>
              <h1>That was unexpected.</h1>
            </PageHeader.Title>
          </PageHeader>
        </ContentContainer>
        <ContentContainer topPadding="none" width="x-large">
          <p>
            Something happened that wasn&apos;t supposed to happen. Please try
            refreshing the browser.
          </p>
        </ContentContainer>
      </MainLayout>
    );
  };

  return WrappedComponent;
};

withPermission.displayName = "withPermission";

export default withPermission;
